import * as React from 'react'
import { Command as CommandPrimitive } from 'cmdk'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'

function MultipleSelector({
  value = [],
  onChange,
  defaultOptions = [],
  placeholder = 'Select...',
  emptyIndicator = <p className="text-center text-sm">No results found</p>,
  hideClearAllButton = false,
  hidePlaceholderWhenSelected = false,
  commandProps,
  className,
  badgeClassName,
  disabled = false,
}) {
  const inputRef = React.useRef(null)
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState('')

  const handleUnselect = React.useCallback(
    (option) => {
      const newValue = value.filter((v) => v.value !== option.value)
      onChange?.(newValue)
    },
    [value, onChange]
  )

  const handleKeyDown = React.useCallback(
    (e) => {
      const input = inputRef.current
      if (input) {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          if (input.value === '' && value.length > 0) {
            const newValue = [...value]
            newValue.pop()
            onChange?.(newValue)
          }
        }
        if (e.key === 'Escape') {
          input.blur()
        }
      }
    },
    [value, onChange]
  )

  const selectables = React.useMemo(() => {
    return defaultOptions.filter(
      (option) => !value.some((v) => v.value === option.value) && !option.disable
    )
  }, [defaultOptions, value])

  const filteredSelectables = React.useMemo(() => {
    if (!inputValue) return selectables
    const searchLower = inputValue.toLowerCase()
    return selectables.filter((option) =>
      option.label.toLowerCase().includes(searchLower)
    )
  }, [selectables, inputValue])

  return (
    <Command
      onKeyDown={handleKeyDown}
      shouldFilter={false}
      className={cn(
        'overflow-visible bg-transparent',
        className
      )}
      {...commandProps}
    >
      <div
        className={cn(
          'group rounded-md border border-input px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <div className="flex flex-wrap gap-1">
          {value.map((option) => (
            <Badge
              key={option.value}
              variant="secondary"
              className={cn(
                'data-[disabled]:bg-muted-foreground data-[disabled]:text-muted data-[disabled]:hover:bg-muted-foreground',
                'data-[fixed]:bg-muted-foreground data-[fixed]:text-muted data-[fixed]:hover:bg-muted-foreground',
                badgeClassName
              )}
            >
              {option.label}
              <button
                className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleUnselect(option)
                  }
                }}
                onMouseDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                onClick={() => handleUnselect(option)}
                disabled={disabled}
              >
                <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
              </button>
            </Badge>
          ))}
          <CommandPrimitive.Input
            ref={inputRef}
            value={inputValue}
            onValueChange={setInputValue}
            onBlur={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            placeholder={
              hidePlaceholderWhenSelected && value.length > 0
                ? ''
                : placeholder
            }
            disabled={disabled}
            className="ml-2 flex-1 bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
          />
          {!hideClearAllButton && value.length > 0 && (
            <button
              type="button"
              onClick={() => onChange?.([])}
              className="ml-auto text-muted-foreground hover:text-foreground"
              disabled={disabled}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <div className="relative mt-2">
        {open && (
          <div className="absolute top-0 z-10 w-full rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in">
            <CommandList>
              {filteredSelectables.length > 0 ? (
                <CommandGroup className="h-full overflow-auto max-h-[200px]">
                  {filteredSelectables.map((option) => (
                    <div
                      key={option.value}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setInputValue('')
                        const newValue = [...value, option]
                        onChange?.(newValue)
                      }}
                      className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                    >
                      {option.label}
                    </div>
                  ))}
                </CommandGroup>
              ) : (
                <div className="py-6 text-center text-sm">{emptyIndicator}</div>
              )}
            </CommandList>
          </div>
        )}
      </div>
    </Command>
  )
}

export default MultipleSelector
export { MultipleSelector }
